import ImageTracer from 'imagetracerjs';
import createManifold from 'manifold-3d';
import manifoldWasmUrl from 'manifold-3d/manifold.wasm?url';
import { BufferAttribute, BufferGeometry, Shape, Vector2 } from 'three';
import { SVGLoader } from 'three/addons/loaders/SVGLoader.js';
import type { GeneratedModel, ModelSettings } from './types';

type ManifoldModule = Awaited<ReturnType<typeof createManifold>>;
type Polygon = Array<[number, number]>;

let manifoldPromise: Promise<ManifoldModule> | undefined;

async function manifold() {
  if (!manifoldPromise) {
    manifoldPromise = createManifold({ locateFile: () => manifoldWasmUrl }).then((module) => {
      module.setup();
      module.setMinCircularEdgeLength(0.1);
      return module;
    });
  }
  return manifoldPromise;
}

function cloneImageData(image: ImageData) {
  return new ImageData(new Uint8ClampedArray(image.data), image.width, image.height);
}

function vectorize(image: ImageData) {
  const options = {
    colorsampling: 0,
    numberofcolors: 2,
    colorquantcycles: 1,
    ltres: 1,
    qtres: 1,
    pathomit: 4,
    rightangleenhance: true,
    blurradius: 0,
    blurdelta: 0,
    scale: 1,
  };
  return ImageTracer.imagedataToSVG(cloneImageData(image), options);
}

function isDark(path: { color?: { r: number; g: number; b: number } }) {
  const color = path.color;
  return Boolean(color && (color.r + color.g + color.b) / 3 < 0.45);
}

function cleanPolygon(points: Vector2[]) {
  const output: Polygon = [];
  for (const point of points) {
    const candidate: [number, number] = [point.x, point.y];
    const previous = output[output.length - 1];
    if (!previous || Math.hypot(previous[0] - candidate[0], previous[1] - candidate[1]) > 0.05) output.push(candidate);
  }
  if (output.length > 2 && Math.hypot(output[0][0] - output[output.length - 1][0], output[0][1] - output[output.length - 1][1]) < 0.05) output.pop();
  return output.length >= 3 ? output : [];
}

function shapesFromSvg(svg: string) {
  const parsed = new SVGLoader().parse(svg);
  const shapes: Shape[] = [];
  for (const path of parsed.paths) {
    if (!isDark(path)) continue;
    shapes.push(...path.toShapes());
  }
  return shapes;
}

function rawContours(shapes: Shape[]) {
  const contours: Polygon[] = [];
  for (const shape of shapes) {
    const outer = cleanPolygon(shape.getPoints(2));
    if (outer.length) contours.push(outer);
    for (const hole of shape.holes) {
      const points = cleanPolygon(hole.getPoints());
      if (points.length) contours.push(points);
    }
  }
  return contours;
}

function bounds(contours: Polygon[]) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  contours.forEach((contour) => contour.forEach(([x, y]) => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }));
  if (!Number.isFinite(minX) || maxX - minX < 0.01 || maxY - minY < 0.01) throw new Error('没有检测到可生成的闭合线条，请调高对比度或补线。');
  return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY };
}

function scaleContours(contours: Polygon[], width: number) {
  const box = bounds(contours);
  const scale = width / box.width;
  const height = box.height * scale;
  return {
    width,
    height,
    contours: contours.map((contour) => contour.map(([x, y]) => [(x - (box.minX + box.maxX) / 2) * scale, ((box.minY + box.maxY) / 2 - y) * scale] as [number, number])),
  };
}

function toGeometry(mesh: { vertProperties: Float32Array; triVerts: Uint32Array }) {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(mesh.vertProperties), 3));
  geometry.setIndex(new BufferAttribute(new Uint32Array(mesh.triVerts), 1));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  return geometry;
}

function withMillimetres(svg: string, width: number, height: number) {
  return svg.replace(/<svg([^>]*)>/, (_match, attrs: string) => {
    const viewBox = attrs.match(/viewBox="[^"]+"/)?.[0] ?? `viewBox="0 0 ${width} ${height}"`;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width.toFixed(3)}mm" height="${height.toFixed(3)}mm" ${viewBox}>`;
  });
}

export async function generateModel(image: ImageData, settings: ModelSettings): Promise<GeneratedModel> {
  const svg = vectorize(image);
  const contours = rawContours(shapesFromSvg(svg));
  const scaled = scaleContours(contours, settings.printWidth);
  const m = await manifold();
  let wire2d: InstanceType<ManifoldModule['CrossSection']> | undefined;
  let base2d: InstanceType<ManifoldModule['CrossSection']> | undefined;
  let wireSolid: InstanceType<ManifoldModule['Manifold']> | undefined;
  let baseSolid: InstanceType<ManifoldModule['Manifold']> | undefined;
  let combinedSolid: InstanceType<ManifoldModule['Manifold']> | undefined;
  try {
    wire2d = new m.CrossSection(scaled.contours, 'EvenOdd').simplify(Math.max(0.01, settings.smoothness / 100));
    if (wire2d.isEmpty()) throw new Error('生成后的线条为空，请降低最小区域过滤值。');
    wireSolid = wire2d.extrude(settings.lineHeight).translate([0, 0, settings.plateType === 'none' ? 0 : settings.plateThickness]);
    let baseGeometry: BufferGeometry | null = null;
    if (settings.plateType !== 'none') {
      if (settings.plateType === 'contour') base2d = wire2d.offset(settings.contourMargin, 'Round').simplify(0.05);
      if (settings.plateType === 'circle') base2d = m.CrossSection.circle(Math.max(scaled.width, scaled.height, settings.plateSize) / 2 + settings.contourMargin, 96);
      if (settings.plateType === 'rectangle') base2d = m.CrossSection.square([Math.max(scaled.width, settings.plateSize) + settings.contourMargin * 2, Math.max(scaled.height, settings.plateSize) + settings.contourMargin * 2], true);
      if (!base2d || base2d.isEmpty()) throw new Error('底板轮廓无法生成，请更换底板类型。');
      baseSolid = base2d.extrude(settings.plateThickness);
      baseGeometry = toGeometry(baseSolid.getMesh());
      combinedSolid = baseSolid.add(wireSolid);
    } else {
      combinedSolid = wireSolid;
    }
    const wireGeometry = toGeometry(wireSolid.getMesh());
    const solidGeometry = toGeometry(combinedSolid.getMesh());
    return {
      svg: withMillimetres(svg, scaled.width, scaled.height),
      wireGeometry,
      baseGeometry,
      solidGeometry,
      width: scaled.width,
      height: scaled.height,
      triangleCount: solidGeometry.getIndex()?.count ? solidGeometry.getIndex()!.count / 3 : solidGeometry.getAttribute('position').count / 3,
    };
  } finally {
    if (combinedSolid && combinedSolid !== wireSolid) combinedSolid.delete();
    baseSolid?.delete();
    wireSolid?.delete();
    base2d?.delete();
    wire2d?.delete();
  }
}
